"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PricingResultSchema = exports.PricingResultLineSchema = exports.PricingInputSchema = exports.PricingLineInputSchema = exports.CompositeSubLineSchema = exports.CartDiscountSchema = exports.LineDiscountSchema = exports.TaxDetailSchema = exports.PosTaxFeeSchema = exports.PosPromotionSchema = exports.PosPromotionRestrictionRulesSchema = exports.PromoConfigSchema = exports.PricingPromoTypeSchema = exports.PosPromotionTypeSchema = void 0;
const zod_1 = require("zod");
/* ── Promotion type unions ──────────────────────────────────────────────
 * FULL union of promo types the POS attaches to cart lines. Only the PRICING
 * subset produces a discount in the engine; the rest are parity no-ops
 * (engine keeps `default: {promoDiscount:0}`). */
exports.PosPromotionTypeSchema = zod_1.z.enum([
    'direct_discount',
    'buy_x_get_y_free',
    'buy_x_get_y_discount',
    'x_for_fixed_price',
    'happy_hour',
    'min_spend',
    'restriction',
    'discount_restriction',
]);
/** Subset the pricing engine's exhaustive `switch` handles. Exhaustiveness is
 *  asserted ONLY over this subset (Phase 3 engine), never the full union. */
exports.PricingPromoTypeSchema = zod_1.z.enum([
    'direct_discount',
    'buy_x_get_y_free',
    'buy_x_get_y_discount',
    'x_for_fixed_price',
    'happy_hour',
]);
/** Frozen-at-scan promo config carried on a cart line (CartItem['promoConfig']). */
exports.PromoConfigSchema = zod_1.z.object({
    name: zod_1.z.string().nullish(),
    type: exports.PosPromotionTypeSchema,
    discountPercent: zod_1.z.number().nullish(),
    discountValue: zod_1.z.number().nullish(),
    packQuantity: zod_1.z.number().nullish(),
    packPrice: zod_1.z.number().nullish(),
    buyQuantity: zod_1.z.number().nullish(),
    getQuantity: zod_1.z.number().nullish(),
    minSpendAmount: zod_1.z.number().nullish(),
    happyHourStart: zod_1.z.string().nullish(),
    happyHourEnd: zod_1.z.string().nullish(),
    happyHourDays: zod_1.z.array(zod_1.z.string()).nullish(),
    stackable: zod_1.z.boolean(),
    mixAndMatch: zod_1.z.boolean().optional(),
}).nullable();
exports.PosPromotionRestrictionRulesSchema = zod_1.z.object({
    blockManualDiscounts: zod_1.z.boolean(),
    blockLoyaltyRedemption: zod_1.z.boolean(),
    blockPromotionsDeals: zod_1.z.boolean(),
    blockCouponCodes: zod_1.z.boolean(),
});
/** Runtime promotion record the POS reads from `/promotions/pos-active`
 *  (AGENTIC_POS/src/api/products.ts PosPromotion). NOT the admin DB schema. */
exports.PosPromotionSchema = zod_1.z.object({
    promotionId: zod_1.z.string(),
    name: zod_1.z.string(),
    type: exports.PosPromotionTypeSchema,
    status: zod_1.z.enum(['active', 'inactive']),
    scheduleStart: zod_1.z.string().nullable(),
    scheduleEnd: zod_1.z.string().nullable(),
    discountPercent: zod_1.z.number().nullish(),
    discountValue: zod_1.z.number().nullish(),
    packQuantity: zod_1.z.number().nullish(),
    packPrice: zod_1.z.number().nullish(),
    stackable: zod_1.z.boolean().optional(),
    mixAndMatch: zod_1.z.boolean().optional(),
    buyQuantity: zod_1.z.number().nullish(),
    getQuantity: zod_1.z.number().nullish(),
    minSpendAmount: zod_1.z.number().nullish(),
    happyHourStart: zod_1.z.string().nullish(),
    happyHourEnd: zod_1.z.string().nullish(),
    happyHourDays: zod_1.z.array(zod_1.z.string()).nullish(),
    restrictionRules: exports.PosPromotionRestrictionRulesSchema.nullable(),
    linkedProductIds: zod_1.z.array(zod_1.z.string()),
    linkedVariantIds: zod_1.z.array(zod_1.z.string()),
});
/** Runtime engine tax/fee definition (AGENTIC_POS/src/api/pos-config.ts TaxFee).
 *  Distinct from the admin `APosTaxFeeSchema` (different field names). */
exports.PosTaxFeeSchema = zod_1.z.object({
    taxFeeId: zod_1.z.string(),
    name: zod_1.z.string(),
    rateType: zod_1.z.enum(['percentage', 'fixed']),
    rateValue: zod_1.z.number(),
    isTaxInclusive: zod_1.z.boolean(),
    isCompound: zod_1.z.boolean(),
    isSalesTax: zod_1.z.boolean(),
    onReceipt: zod_1.z.boolean(),
    priority: zod_1.z.number(),
    effectiveFrom: zod_1.z.string().nullish(),
    effectiveTo: zod_1.z.string().nullish(),
    isActive: zod_1.z.boolean(),
    isFee: zod_1.z.boolean(),
});
/** A single computed tax/fee row on a line (taxEngine.ts TaxDetail). */
exports.TaxDetailSchema = zod_1.z.object({
    taxFeeId: zod_1.z.string(),
    name: zod_1.z.string(),
    rateType: zod_1.z.enum(['percentage', 'fixed']),
    rateValue: zod_1.z.number(),
    amount: zod_1.z.number(),
    isSalesTax: zod_1.z.boolean(),
    isCompound: zod_1.z.boolean(),
    onReceipt: zod_1.z.boolean(),
});
/* ── Discount inputs ────────────────────────────────────────────────────── */
const AppliedBySchema = zod_1.z.object({ employeeId: zod_1.z.string(), employeeName: zod_1.z.string() });
exports.LineDiscountSchema = zod_1.z.object({
    type: zod_1.z.enum(['percentage', 'fixed']),
    value: zod_1.z.number(),
    label: zod_1.z.string().optional(),
    source: zod_1.z.enum(['manual', 'promotion', 'employee']).optional(),
    promotionId: zod_1.z.string().optional(),
    promotionName: zod_1.z.string().optional(),
    reason: zod_1.z.string().optional(),
    appliedBy: AppliedBySchema.optional(),
});
exports.CartDiscountSchema = zod_1.z.object({
    type: zod_1.z.enum(['percentage', 'fixed']),
    value: zod_1.z.number(),
    label: zod_1.z.string().optional(),
    source: zod_1.z.enum(['manual', 'promotion', 'loyalty', 'employee']).optional(),
    promotionId: zod_1.z.string().optional(),
    promotionName: zod_1.z.string().optional(),
    reason: zod_1.z.string().optional(),
    appliedBy: AppliedBySchema.optional(),
    loyaltyPointsRedeemed: zod_1.z.number().optional(),
});
/* ── Composite (oil-blend) sub-line ─────────────────────────────────────── */
exports.CompositeSubLineSchema = zod_1.z.object({
    kind: zod_1.z.enum(['bottle', 'fragrance', 'carrier']),
    lineId: zod_1.z.string(),
    variantId: zod_1.z.string(),
    productId: zod_1.z.string(),
    displayName: zod_1.z.string(),
    quantity: zod_1.z.number(),
    unitPrice: zod_1.z.number(),
    grossTotal: zod_1.z.number(),
    costPrice: zod_1.z.number(),
    categoryId: zod_1.z.string().nullish(),
    productTypeId: zod_1.z.string().nullish(),
    taxIds: zod_1.z.array(zod_1.z.string()),
    compliancePresetId: zod_1.z.string().nullish(),
    isAgeRestricted: zod_1.z.boolean(),
    discountAmount: zod_1.z.number().optional(),
    lineTotal: zod_1.z.number().optional(),
});
/* ── Pricing input (what the web builds for the shared engine) ──────────────
 * Fields recalculate reads + the output fields web seeds to []/0. Non-obvious
 * defaults (cartItemType/transactionMode 'sale') are explicit per spec §1.2. */
exports.PricingLineInputSchema = zod_1.z.object({
    lineId: zod_1.z.string(),
    unitPrice: zod_1.z.number(),
    quantity: zod_1.z.number(),
    isWeighted: zod_1.z.boolean().default(false),
    weight: zod_1.z.number().nullable().default(null),
    taxIds: zod_1.z.array(zod_1.z.string()).default([]),
    feeIds: zod_1.z.array(zod_1.z.string()).default([]),
    lineDiscount: exports.LineDiscountSchema.nullable().default(null),
    promotionId: zod_1.z.string().nullable().default(null),
    promoConfig: exports.PromoConfigSchema.default(null),
    discountRestricted: zod_1.z.boolean().default(false),
    cartItemType: zod_1.z.enum(['sale', 'return', 'damage']).default('sale'),
    compositeType: zod_1.z.literal('blend').optional(),
    subLines: zod_1.z.array(exports.CompositeSubLineSchema).optional(),
    // Output fields the web seeds so the engine's tax fallback stays byte-identical.
    grossTotal: zod_1.z.number().default(0),
    taxBreakdown: zod_1.z.array(exports.TaxDetailSchema).default([]),
    totalTaxAmount: zod_1.z.number().default(0),
    feeBreakdown: zod_1.z.array(exports.TaxDetailSchema).default([]),
    totalFeeAmount: zod_1.z.number().default(0),
});
exports.PricingInputSchema = zod_1.z.object({
    items: zod_1.z.array(exports.PricingLineInputSchema),
    cartDiscount: exports.CartDiscountSchema.nullable().default(null),
    transactionMode: zod_1.z.enum(['sale', 'exchange', 'refund', 'damage']).default('sale'),
});
/* ── Pricing result (subset of recalculate's returned CartState) ─────────── */
exports.PricingResultLineSchema = zod_1.z.object({
    lineId: zod_1.z.string(),
    grossTotal: zod_1.z.number(),
    discountAmount: zod_1.z.number(),
    promoDiscountAmount: zod_1.z.number().optional(),
    manualDiscountAmount: zod_1.z.number().optional(),
    lineTotal: zod_1.z.number(),
    taxBreakdown: zod_1.z.array(exports.TaxDetailSchema),
    feeBreakdown: zod_1.z.array(exports.TaxDetailSchema),
    totalTaxAmount: zod_1.z.number(),
    totalFeeAmount: zod_1.z.number(),
    promoDealActive: zod_1.z.boolean(),
});
exports.PricingResultSchema = zod_1.z.object({
    netTotal: zod_1.z.number(),
    taxTotal: zod_1.z.number(),
    salesTaxTotal: zod_1.z.number(),
    otherTaxTotal: zod_1.z.number(),
    itemDiscountTotal: zod_1.z.number(),
    orderDiscountTotal: zod_1.z.number(),
    feesTotal: zod_1.z.number(),
    grandTotal: zod_1.z.number(),
    itemCount: zod_1.z.number(),
    returnTotal: zod_1.z.number(),
    saleItemsTotal: zod_1.z.number(),
    netExchangeAmount: zod_1.z.number(),
    items: zod_1.z.array(exports.PricingResultLineSchema),
});
//# sourceMappingURL=pos-pricing.schema.js.map